import React, { useState, useEffect, useCallback} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import NewsItem from './NewsItem';
import './ProductPage.css';

const ProductPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('politics');
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState('');
  const [loadingItemId, setLoadingItemId] = useState(null);
  const [newsItems, setNewsItems] = useState({});
  const [loadingCategories, setLoadingCategories] = useState({});
  const [isShuffling, setIsShuffling] = useState(false);

  const tabs = [
    { id: 'politics', label: 'Political ', icon: '🏛️' },
    { id: 'tech', label: 'Technology', icon: '💻' },
    { id: 'business', label: 'Business & Finance', icon: '📈' },
    { id: 'science', label: 'Science & Health', icon: '🔬' }
  ];

  // Fetch news for a specific category
  const fetchNewsForCategory = useCallback(async (category, forceRefresh = false) => {
    if (newsItems[category] && !forceRefresh) {
      return; // Already loaded and not forcing refresh
    }

    setLoadingCategories(prev => ({ ...prev, [category]: true }));
    
    try {
      const response = await fetch('https://podtalk.onrender.com/generate-news', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ category }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch news');
      }

      const data = await response.json();
      setNewsItems(prev => ({
        ...prev,
        [category]: data.news
      }));
    } catch (err) {
      console.error(`Error fetching news for ${category}:`, err);
      setError(`Failed to load ${category} news. Please try again.`);
    } finally {
      setLoadingCategories(prev => ({ ...prev, [category]: false }));
    }
  },
  [newsItems]
);

  // Shuffle/refresh current category news
  const handleShuffle = async () => {
    setIsShuffling(true);
    setError('');
    
    try {
      // Force refresh the current category
      await fetchNewsForCategory(activeTab, true);
    } catch (err) {
      console.error('Error shuffling news:', err);
      setError('Failed to refresh news. Please try again.');
    } finally {
      setIsShuffling(false);
    }
  };

  // Load initial news when component mounts
  useEffect(() => {
    fetchNewsForCategory('politics');
  }, [fetchNewsForCategory]);

  // Load news when tab changes
  useEffect(() => {
    fetchNewsForCategory(activeTab);
  }, [fetchNewsForCategory,activeTab]);

  // Helper function to determine if input is a URL
  const isUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    setError('');
    
    try {
      // Check if the query is a URL
      const isUrlInput = isUrl(searchQuery.trim());
      
      let requestBody;
      
      if (isUrlInput) {
        // If it's a URL, pass it as URLs parameter
        requestBody = {
          urls: [searchQuery.trim()],
          longform: true
        };
      } else {
        // If it's a keyword/topic, create a comprehensive text prompt for research
        const searchPrompt = `
          Please research and provide comprehensive information about: "${searchQuery.trim()}"
          
          Include:
          - Latest developments and current information
          - Key facts and statistics
          - Multiple perspectives and expert opinions
          - Recent news and updates
          - Context and background information
          - Future implications and trends
          
          Please provide a thorough analysis suitable for an informative audio summary.
        `;
        
        requestBody = {
          text: searchPrompt,
          longform: true
        };
      }

      // Use the existing generate-podcast endpoint
      const response = await fetch('https://podtalk.onrender.com/generate-podcast', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error('Failed to generate podcast');
      }

      // Handle audio blob response
      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      
      // Determine title based on input type
      const title = isUrlInput 
        ? `Analysis: ${searchQuery.trim()}` 
        : `Research: ${searchQuery.trim()}`;
      
      const description = isUrlInput 
        ? 'AI-generated analysis of the provided URL content'
        : 'AI-generated research summary based on your search query';
      
      // Navigate to playing page with audio data
      navigate('/playing', {
        state: {
          audioSrc: audioUrl,
          title: title,
          description: description
        }
      });
    } catch (err) {
      setError('Failed to generate podcast. Please try again.');
      console.error('Error:', err);
    } finally {
      setIsSearching(false);
    }
  };

  const handleGenerateAudio = async (item) => {
    setLoadingItemId(item.id);
    setError('');
    
    try {
      // Create comprehensive text content from the news item
      const textContent = `
        News Title: ${item.title}
        
        Summary: ${item.summary}
        
        Source: ${item.source}
        
        Please provide a detailed analysis and discussion of this news story, including context, implications, and potential future developments.
      `.trim();

      const response = await fetch('https://podtalk.onrender.com/generate-podcast', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          text: textContent,
          longform: false 
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate podcast');
      }

      // Handle audio blob response
      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      
      // Navigate to playing page with audio data
      navigate('/playing', {
        state: {
          audioSrc: audioUrl,
          title: item.title,
          description: item.summary
        }
      });
    } catch (err) {
      setError('Failed to generate podcast. Please try again.');
      console.error('Error:', err);
    } finally {
      setLoadingItemId(null);
    }
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    // Clear any existing errors when switching tabs
    setError('');
  };

  const currentNews = newsItems[activeTab] || [];

  return (
    <div className="product-page">
      {/* Header Navigation */}
      <header className="product-header">
        <div className="header-content">
          <Link to="/" className="logo">
            <h2>PodTalk</h2>
          </Link>
          <nav className="header-nav">
            <Link to="/" className="nav-link">← Back to Home</Link>
          </nav>
        </div>
      </header>

      {/* Hero Search Section */}
      <section className="search-hero">
        <div className="search-content">
          <h1>What would you like to explore today?</h1>
          <p>Enter a topic, keyword, or URL to get a comprehensive AI-powered audio summary</p>
          <form onSubmit={handleSearch} className="search-form">
            <div className="search-input-container">
              <input
                type="text"
                placeholder="Enter a topic, keyword, or paste a URL here..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
                disabled={isSearching}
              />
              <button type="submit" className="search-button" disabled={isSearching || !searchQuery.trim()}>
                {isSearching ? (
                  <span className="loading-spinner"></span>
                ) : (
                  <>
                    <span className="search-icon">🔍</span>
                    Generate PodTalk
                  </>
                )}
              </button>
            </div>
          </form>
          <div className="search-examples">
            <p>Try: "Latest AI developments", "Climate change 2024", or paste any news article URL</p>
          </div>
        </div>
      </section>

      {/* Tabs Navigation */}
      <section className="tabs-section">
        <div className="tabs-container">
          <h2>Or explore curated summaries by category</h2>
          <div className="tabs">
            {tabs.map(tab => (
              <button
                key={tab.id}
                className={`tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => handleTabChange(tab.id)}
              >
                <span className="tab-icon">{tab.icon}</span>
                <span className="tab-label">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* News Feed */}
      <section className="news-feed">
        <div className="news-container">
          <div className="feed-header">
            <div className="feed-title">
              <h3>{tabs.find(tab => tab.id === activeTab)?.label}</h3>
              <p>Latest curated summaries from trusted sources</p>
            </div>
            <button 
              className={`shuffle-button ${isShuffling ? 'shuffling' : ''}`}
              onClick={handleShuffle}
              disabled={isShuffling || loadingCategories[activeTab]}
              title="Get fresh news for this category"
            >
              {isShuffling ? (
                <>
                  <span className="loading-spinner-small"></span>
                  Refreshing...
                </>
              ) : (
                <>
                  <span className="shuffle-icon">🔄</span>
                  Refresh News
                </>
              )}
            </button>
          </div>
          
          {/* Loading State */}
          {loadingCategories[activeTab] && !isShuffling && (
            <div className="loading-state">
              <div className="loading-spinner-large"></div>
              <p>Generating fresh content with AI...</p>
            </div>
          )}
          
          {/* News Grid */}
          {!loadingCategories[activeTab] && (
            <div className="news-grid">
              {currentNews.length > 0 ? (
                currentNews.map(item => (
                  <NewsItem
                    key={item.id}
                    category={item.category}
                    id={item.id}
                    title={item.title}
                    image={item.image}
                    summary={item.summary}
                    timestamp={item.timestamp}
                    source={item.source}
                    onGenerateAudio={handleGenerateAudio}
                    isLoading={loadingItemId === item.id}
                  />
                ))
              ) : (
                <div className="no-news">
                  <p>No news available for this category. Please try again later.</p>
                  <button 
                    className="retry-button"
                    onClick={() => fetchNewsForCategory(activeTab, true)}
                  >
                    Retry
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Error Message */}
      {error && (
        <div className="error-container">
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            {error}
            <button onClick={() => setError('')} className="error-close">✕</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductPage; 